/**
 * empire-colors.ts — fixed-palette empire colour catalogue.
 *
 * Replaces the v4M1.9 free-RGB colour picker in `new-game-setup.ts`
 * with a designer-curated 8-entry palette. Two motivations:
 *
 *   1. **Render-time predictability.** The empire-tint pass (Option B
 *      multiply) and the per-(race, chassis, color) sprite variant
 *      path (Option C — author-baked colour rings) both expect the
 *      empire colour to be one of a known short list. A fully free
 *      picker lets the player choose colours that fight the source
 *      sprite (e.g. a navy-blue empire muddying a red-accent sprite).
 *   2. **Per-color sprite variants.** Each colour ID below is the
 *      filename suffix the loader probes for in `tryLoadCommissioned
 *      ShipSprites` (e.g. `race-aa-scout-red.png`). The probe loop
 *      iterates this palette, so adding a colour here automatically
 *      extends the probe.
 *
 * Numeric IDs are intentionally short lowercase strings — they appear
 * in filenames and any future per-color routing. Hex values are the
 * exact RGB Michael provided 2026-05-23.
 *
 * Palette grows as players are added. Current 8-entry palette covers
 * up to 8 simultaneous empires (1 player + 7 AI) — the maximum the
 * new-game-setup picker will currently allow. If the cap is raised
 * later, extend this list and the new-game-setup AI slider's max.
 */

export interface EmpireColor {
  /** Filename-safe lowercase identifier (e.g. `'red'`, `'orange'`). */
  readonly id: string;
  /** Hex string `#RRGGBB` (capitalised hex letters to match `RACE_ACCENT` convention). */
  readonly hex: string;
  /** Human-readable label shown in the swatch tooltip. */
  readonly label: string;
}

/**
 * The locked palette. Order is deliberate — first 5 are the "warm"
 * cohort Michael delivered first; last 3 are the "cool" cohort
 * authored alongside. Players default to `palette[0]` (Red); AI slots
 * cycle through `palette[1..N-1]`.
 */
export const EMPIRE_COLOR_PALETTE: readonly EmpireColor[] = Object.freeze([
  { id: 'red',    hex: '#FF0000', label: 'Red' },
  { id: 'orange', hex: '#FF8A00', label: 'Orange' },
  { id: 'yellow', hex: '#FFF500', label: 'Yellow' },
  { id: 'pink',   hex: '#FF00A8', label: 'Pink' },
  { id: 'purple', hex: '#7000FF', label: 'Purple' },
  { id: 'green',  hex: '#00FF21', label: 'Green' },
  { id: 'cyan',   hex: '#00FFF0', label: 'Cyan' },
  { id: 'blue',   hex: '#004FFF', label: 'Blue' },
]);

/**
 * Hex of the palette entry at `index`, wrapping modulo palette length
 * so callers can pass arbitrary slot indices without bounds-checking.
 */
export function paletteHexAt(index: number): string {
  const palette = EMPIRE_COLOR_PALETTE;
  return palette[((index % palette.length) + palette.length) % palette.length]!.hex;
}

/**
 * Returns the palette `id` matching the given hex (case-insensitive
 * compare on the hex string). Returns `null` for hex values not in
 * the palette — used by the loader to derive the per-color sprite
 * filename suffix at probe time. A hex outside the palette falls
 * through to the neutral-sprite + multiply-tint path.
 */
export function paletteIdFromHex(hex: string): string | null {
  const upper = hex.toUpperCase();
  for (const entry of EMPIRE_COLOR_PALETTE) {
    if (entry.hex.toUpperCase() === upper) return entry.id;
  }
  return null;
}

/**
 * Pick the first palette hex not in `taken`. Used by the new-game-
 * setup swap-on-conflict logic: when an empire claims a colour
 * another empire was using, the other empire bumps to the first free
 * slot via this helper. Returns `null` when the entire palette is
 * occupied — surface as a UI hard-cap on simultaneous empires.
 */
export function firstFreePaletteHex(taken: readonly string[]): string | null {
  const upperTaken = new Set(taken.map((h) => h.toUpperCase()));
  for (const entry of EMPIRE_COLOR_PALETTE) {
    if (!upperTaken.has(entry.hex.toUpperCase())) return entry.hex;
  }
  return null;
}
